import { HardHat, Printer, Heart, Scale, ShoppingCart, Shield, Tractor, UtensilsCrossed, Building, Wrench } from 'lucide-react';
import type { Product } from './types';

export const industryVerticalProducts: Product[] = [
  {
    id: 'fieldwire-construction',
    title: 'Fieldwire Enterprise Construction Suite',
    subtitle: 'Advanced construction management platform',
    description: 'Enterprise-grade construction management solution with advanced checklists, inspections, real-time messaging, scheduling, multi-site management, and comprehensive reporting.',
    icon: HardHat,
    image: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?auto=format&fit=crop&w=800&q=80',
    category: 'Industry Verticals',
    status: 'Available',
    businessTypes: ['#construction', '#project-management', '#field-operations', '#inspections', '#scheduling'],
    features: [
      'Digital blueprints and plan management',
      'Advanced task and inspection checklists',
      'Real-time team communication',
      'Photo documentation and markups',
      'Advanced scheduling and progress tracking',
      'Compliance and safety reporting',
      'Multi-site project management',
      'Advanced reporting and analytics',
      'Integration with enterprise systems',
      'Mobile workforce management'
    ],
    pricing: { setup: 42000, monthly: 1932 },
    color: 'from-orange-500 to-red-500',
    demoConfig: {
      type: 'showcase',
      url: '/demo/fieldwire-construction',
      title: 'Construction Management Demo',
      description: 'Experience how Fieldwire streamlines construction project management',
      buttonText: 'View Construction Demo',
      icon: HardHat,
      buttonColor: 'from-orange-500 to-red-500'
    }
  },
  {
    id: 'enterprise-print-manufacturing',
    title: 'Enterprise Print-on-Demand & Custom Manufacturing Platform',
    subtitle: 'B2B custom manufacturing solution',
    description: 'Enterprise-grade print-on-demand and custom manufacturing platform with advanced API access, white-label solutions, bulk ordering, and quality control systems.',
    icon: Printer,
    image: 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?auto=format&fit=crop&w=800&q=80',
    category: 'Industry Verticals',
    status: 'Available',
    businessTypes: ['#enterprise-manufacturing', '#b2b-printing', '#custom-products', '#white-label', '#bulk-ordering'],
    features: [
      'Enterprise-grade production capacity',
      'Advanced API and integration suite',
      'White-label platform customization',
      'Quality control and assurance systems',
      'Bulk order management',
      'Custom manufacturing workflows',
      'Advanced design and mockup tools',
      'Multi-location fulfillment network',
      'Enterprise reporting and analytics',
      'Dedicated account management'
    ],
    pricing: { setup: 45000, monthly: 2070 },
    color: 'from-purple-500 to-pink-500'
  },
  {
    id: 'healthcare-compliance-management',
    title: 'Healthcare Compliance Management',
    subtitle: 'HIPAA & Regulatory Platform',
    description: 'Specialized compliance platform helping healthcare organizations maintain HIPAA and other regulatory compliance through automated monitoring.',
    icon: Heart,
    image: 'https://images.unsplash.com/photo-1559757148-5c350d0d3c56?auto=format&fit=crop&w=800&q=80',
    category: 'Industry Verticals',
    status: 'Available',
    businessTypes: ['#healthcare', '#compliance', '#hipaa', '#regulatory', '#medical'],
    features: [
      'HIPAA compliance automation',
      'Risk assessment and remediation',
      'Staff training and certification',
      'Incident tracking and reporting',
      'Audit trail management',
      'Policy and procedure management'
    ],
    pricing: { setup: 48000, monthly: 2208 },
    color: 'from-red-500 to-pink-500'
  },
  {
    id: 'legal-document-analytics',
    title: 'Legal Document Analytics',
    subtitle: 'AI Legal Intelligence',
    description: 'Advanced NLP platform that analyses legal documents, extracts key provisions, and identifies potential risks and anomalies in contracts with UK legal standards.',
    icon: Scale,
    image: 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?auto=format&fit=crop&w=800&q=80',
    category: 'Industry Verticals',
    status: 'Available',
    businessTypes: ['#legal-tech', '#contract-analysis', '#nlp', '#risk-assessment', '#law-firms'],
    features: [
      'Automated contract review and analysis',
      'Risk identification and scoring',
      'Clause extraction and categorization',
      'Legal precedent research',
      'Compliance checking automation',
      'Document comparison and redlining'
    ],
    pricing: { setup: 52000, monthly: 2392 },
    color: 'from-blue-500 to-indigo-500'
  },
  {
    id: 'retail-assortment-planning',
    title: 'Retail Assortment Planning',
    subtitle: 'Merchandise Optimization',
    description: 'Optimises product selection and placement based on local demand patterns, inventory levels, and margin targets for retail success.',
    icon: ShoppingCart,
    image: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?auto=format&fit=crop&w=800&q=80',
    category: 'Industry Verticals',
    status: 'Available',
    businessTypes: ['#retail', '#merchandise-planning', '#inventory-optimization', '#demand-forecasting', '#assortment'],
    features: [
      'Demand forecasting and planning',
      'Category and assortment optimisation',
      'Price optimisation and margin analysis',
      'Seasonal and trend analysis',
      'Store-specific recommendations',
      'Performance tracking and analytics'
    ],
    pricing: { setup: 42000, monthly: 1932 },
    color: 'from-green-500 to-emerald-500'
  },
  {
    id: 'insurance-claims-automation',
    title: 'Insurance Claims Automation',
    subtitle: 'AI Claims Processing',
    description: 'Automated insurance claims processing using AI for image recognition, damage assessment, and fraud detection to accelerate settlements.',
    icon: Shield,
    image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?auto=format&fit=crop&w=800&q=80',
    category: 'Industry Verticals',
    status: 'Available',
    businessTypes: ['#insurance', '#claims-processing', '#automation', '#fraud-detection', '#image-recognition'],
    features: [
      'Automated damage assessment',
      'Fraud detection and prevention',
      'Image recognition and analysis',
      'Claims routing and prioritization',
      'Settlement automation',
      'Regulatory compliance tracking'
    ],
    pricing: { setup: 58000, monthly: 2668 },
    color: 'from-cyan-500 to-blue-500'
  },
  {
    id: 'restaurant-inventory-management',
    title: 'Enterprise Restaurant Inventory & Analytics Suite',
    subtitle: 'Advanced food service operations platform',
    description: 'Enterprise-grade inventory management for restaurants with AI-powered demand forecasting, multi-location support, supplier integration, and advanced analytics to minimize waste and maximize profitability.',
    icon: UtensilsCrossed,
    image: 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?auto=format&fit=crop&w=800&q=80',
    category: 'Industry Verticals',
    status: 'Available',
    businessTypes: ['#restaurant', '#food-service', '#inventory-management', '#waste-reduction', '#cost-control'],
    features: [
      'Real-time inventory tracking',
      'AI-powered demand forecasting',
      'Automated reorder point calculation',
      'Advanced waste tracking and reduction',
      'Recipe costing and profitability analysis',
      'Multi-location inventory management',
      'Supplier integration and ordering automation',
      'Food safety and expiration monitoring',
      'Advanced analytics and reporting',
      'Cost optimisation recommendations'
    ],
    pricing: { setup: 38000, monthly: 1748 },
    color: 'from-orange-500 to-red-500'
  },
  {
    id: 'hotel-revenue-management',
    title: 'Hotel Revenue Management',
    subtitle: 'Hospitality Optimization',
    description: 'Dynamic pricing and revenue optimization platform that adjusts room rates based on demand patterns, competitor pricing, and market conditions.',
    icon: Building,
    image: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?auto=format&fit=crop&w=800&q=80',
    category: 'Industry Verticals',
    status: 'Available',
    businessTypes: ['#hospitality', '#revenue-management', '#dynamic-pricing', '#hotel-operations', '#yield-optimization'],
    features: [
      'Dynamic pricing algorithms',
      'Demand forecasting and analysis',
      'Competitor rate monitoring',
      'Channel management integration',
      'Revenue optimisation recommendations',
      'Performance reporting and analytics'
    ],
    pricing: { setup: 48000, monthly: 2208 },
    color: 'from-teal-500 to-green-500'
  },
  {
    id: 'manufacturing-quality-management',
    title: 'Manufacturing Quality Management',
    subtitle: 'Production Quality Control',
    description: 'Comprehensive quality management system that captures and analyses quality data from production processes to identify defects and improve outcomes with UK manufacturing standards.',
    icon: Wrench,
    image: 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?auto=format&fit=crop&w=800&q=80',
    category: 'Industry Verticals',
    status: 'Available',
    businessTypes: ['#manufacturing', '#quality-control', '#production', '#defect-tracking', '#process-improvement'],
    features: [
      'Real-time quality monitoring',
      'Statistical process control',
      'Defect tracking and analysis',
      'Corrective action management',
      'Supplier quality management',
      'Compliance and audit reporting'
    ],
    pricing: { setup: 52000, monthly: 2392 },
    color: 'from-violet-500 to-purple-500'
  }
];
